import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import { normalizeUsername, isUsernameTaken } from '../../lib/usernames';

export default function SignupPage() {
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(''); // 'google' | 'apple'

  // Username availability state
  const [usernameStatus, setUsernameStatus] = useState('idle'); // 'idle' | 'checking' | 'available' | 'taken' | 'invalid'
  const usernameDebounce = useRef(null);

  const USERNAME_REGEX = /^[a-z0-9_]{3,20}$/;

  useEffect(() => {
    if (!username) {
      setUsernameStatus('idle');
      return;
    }

    if (!USERNAME_REGEX.test(username)) {
      setUsernameStatus('invalid');
      return;
    }

    setUsernameStatus('checking');
    clearTimeout(usernameDebounce.current);
    usernameDebounce.current = setTimeout(async () => {
      setUsernameStatus(await isUsernameTaken(username) ? 'taken' : 'available');
    }, 500);

    return () => clearTimeout(usernameDebounce.current);
  }, [username]);

  const handleUsernameChange = (e) => {
    // Same cleaning the signup trigger applies, so the name checked for
    // availability is the name that ends up stored. Stripping only spaces let
    // someone type "jane.austen", be told it was available, and be given
    // "janeausten" by the trigger — possibly with digits appended, because
    // that one was already taken.
    setUsername(normalizeUsername(e.target.value));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!USERNAME_REGEX.test(username)) {
      setError('Username must be 3–20 characters: lowercase letters, numbers, and underscores only.');
      return;
    }
    if (usernameStatus !== 'available') {
      setError('Please choose an available username before continuing.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setLoading(true);
    const { error: signUpError, data } = await signUp(email, password, displayName, username);
    if (signUpError) {
      setError(signUpError.message);
      setLoading(false);
    } else {
      if (data?.user) {
        // Neither of these was checked. The auth account is already created by
        // this point, so a failure here left a signed-up user with no role
        // and/or no profile row — no username, and whatever gates on
        // user_roles closed against them — while the screen sent them happily
        // on to /verify-email. A broken account that reports success is worse
        // than a signup that fails, because nothing prompts anyone to look.
        //
        // Not fatal to the signup itself, so this does not throw: it warns and
        // continues, and says what to do. Whether a database trigger backfills
        // these rows could not be confirmed from the migrations in this repo.
        const { error: roleErr } = await supabase
          .from('user_roles')
          .upsert(
            { user_id: data.user.id, role: 'reader' },
            { onConflict: 'user_id,role', ignoreDuplicates: true }
          );

        // Create profile with the chosen username
        const { error: profileErr } = await supabase
          .from('user_profiles')
          .upsert(
            {
              user_id: data.user.id,
              username,
              display_name: displayName,
            },
            { onConflict: 'user_id', ignoreDuplicates: true }
          );
        if (roleErr || profileErr) {
          setError(
            'Your account was created, but part of your profile did not save' +
            `${profileErr ? ` (${profileErr.message})` : ''}` +
            '. Verify your email, then check your username in Settings, and tell us if it is wrong.',
          );
        }
      }
      navigate('/verify-email', { state: { email } });
    }
  };

  async function handleOAuth(provider) {
    setOauthLoading(provider);
    setError('');
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/`,
        ...(provider === 'apple' && {
          queryParams: { response_mode: 'form_post' },
        }),
      },
    });
    if (error) {
      setError(error.message);
      setOauthLoading('');
    }
  }

  // Username hint text + color
  const usernameHint = {
    idle: null,
    invalid: { text: 'Use 3–20 characters: lowercase letters, numbers, underscores only.', color: 'var(--feedback-warning-ink)' },
    checking: { text: 'Checking availability…', color: 'var(--neutral-500)' },
    available: { text: '✓ Username is available', color: '#15803d' },
    taken: { text: '✗ Username is already taken', color: 'var(--feedback-error)' },
  }[usernameStatus];

  const inputStyle = {
    width: '100%', padding: '11px 14px', borderRadius: 'var(--radius-10)',
    border: '1px solid var(--brand-sand)', fontSize: 'var(--font-size-md)', color: 'var(--neutral-800)',
    outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: 'var(--brand-paper)',
  };

  const oauthBtnBase = {
    width: '100%', padding: '11px 14px', borderRadius: 'var(--radius-10)', border: '1px solid var(--brand-sand)',
    fontSize: 'var(--font-size-base)', fontWeight: 600, cursor: 'pointer', display: 'flex',
    alignItems: 'center', justifyContent: 'center', gap: 'var(--space-10)',
    background: 'var(--brand-white)', color: 'var(--neutral-800)', transition: 'all 0.15s',
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--brand-paper)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-xxl)',fontFamily: 'var(--font-ui)' }}>
      <div style={{ width: '100%', maxWidth: 400 }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-h3)' }}>
          <Link to="/" style={{ textDecoration: 'none' }}>
            <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--space-xs)' }}>
              <img src="/logo2.png" alt="Reanlo" style={{ width: 160, height: 48, objectFit: 'cover' }} />
              <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-400)', margin: 'var(--space-none)' }}>Reading beyond the Algorithm</p>
            </div>
          </Link>
        </div>

        <div style={{ background: 'var(--brand-white)', borderRadius: 'var(--radius-xxl)', padding: 'var(--space-h3)', border: '1px solid var(--brand-sand)', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 6px' }}>Create your account</h2>
          <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', margin: '0 0 24px' }}>Free forever. No algorithms. Just great books.</p>

          {error && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 'var(--radius-10)', padding: '10px 14px', marginBottom: 'var(--space-xl)' }}>
              <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--feedback-error)', margin: 'var(--space-none)' }}>{error}</p>
            </div>
          )}

          {/* ── OAuth Buttons ─────────────────────────────────────────── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-10)', marginBottom: 'var(--space-xl)' }}>
            <button
              onClick={() => handleOAuth('google')}
              disabled={!!oauthLoading}
              style={{ ...oauthBtnBase, opacity: oauthLoading && oauthLoading !== 'google' ? 0.5 : 1 }}
              onMouseEnter={e => e.currentTarget.style.background = '#faf8f5'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}
            >
              {oauthLoading === 'google' ? (
                <span style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)' }}>Connecting...</span>
              ) : (
                <><GoogleIcon /> Continue with Google</>
              )}
            </button>

            <button
              onClick={() => handleOAuth('apple')}
              disabled={!!oauthLoading}
              style={{ ...oauthBtnBase, background: 'var(--brand-black)', color: 'var(--brand-white)', border: '1px solid var(--brand-black)', opacity: oauthLoading && oauthLoading !== 'apple' ? 0.5 : 1 }}
              onMouseEnter={e => e.currentTarget.style.background = '#1a1a1a'}
              onMouseLeave={e => e.currentTarget.style.background = '#000'}
            >
              {oauthLoading === 'apple' ? (
                <span style={{ fontSize: 'var(--font-size-base)' }}>Connecting...</span>
              ) : (
                <><AppleIcon /> Continue with Apple</>
              )}
            </button>
          </div>

          {/* ── Divider ───────────────────────────────────────────────── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', marginBottom: 'var(--space-xl)' }}>
            <div style={{ flex: 1, height: 1, background: 'var(--brand-sand)' }} />
            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', fontWeight: 500 }}>or sign up with email</span>
            <div style={{ flex: 1, height: 1, background: 'var(--brand-sand)' }} />
          </div>

          {/* ── Email Form ────────────────────────────────────────────── */}
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 'var(--space-lg)' }}>
              <label style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, color: 'var(--neutral-800)', display: 'block', marginBottom: 'var(--space-6)' }}>Your name</label>
              <input
                type="text" value={displayName} onChange={e => setDisplayName(e.target.value)}
                placeholder="Jane Austen" required style={inputStyle}
                onFocus={e => e.target.style.borderColor = '#d97706'}
                onBlur={e => e.target.style.borderColor = '#e8e4dc'}
              />
            </div>

            <div style={{ marginBottom: 'var(--space-lg)' }}>
              <label style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, color: 'var(--neutral-800)', display: 'block', marginBottom: 'var(--space-6)' }}>
                Username
              </label>
              <div style={{ position: 'relative' }}>
                <span style={{
                  position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)',
                  fontSize: 'var(--font-size-md)', color: 'var(--neutral-400)', pointerEvents: 'none',
                }}>@</span>
                <input
                  type="text"
                  value={username}
                  onChange={handleUsernameChange}
                  placeholder="janeausten"
                  required
                  maxLength={20}
                  style={{
                    ...inputStyle,
                    paddingLeft: 28,
                    borderColor: usernameStatus === 'available' ? '#15803d'
                      : usernameStatus === 'taken' || usernameStatus === 'invalid' ? 'var(--feedback-error)'
                      : 'var(--brand-sand)',
                  }}
                  onFocus={e => {
                    if (usernameStatus === 'idle' || usernameStatus === 'checking') {
                      e.target.style.borderColor = '#d97706';
                    }
                  }}
                  onBlur={e => {
                    if (usernameStatus === 'idle') e.target.style.borderColor = '#e8e4dc';
                  }}
                />
              </div>
              {usernameHint && (
                <p style={{ fontSize: 'var(--font-size-sm)', color: usernameHint.color, margin: '5px 0 0', lineHeight: 1.4 }}>
                  {usernameHint.text}
                </p>
              )}
              {!usernameHint && (
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', margin: '5px 0 0' }}>
                  This is your public handle: lowercase letters, numbers, underscores.
                </p>
              )}
            </div>

            <div style={{ marginBottom: 'var(--space-lg)' }}>
              <label style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, color: 'var(--neutral-800)', display: 'block', marginBottom: 'var(--space-6)' }}>Email</label>
              <input
                type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com" required style={inputStyle}
                onFocus={e => e.target.style.borderColor = '#d97706'}
                onBlur={e => e.target.style.borderColor = '#e8e4dc'}
              />
            </div>

            <div style={{ marginBottom: 'var(--space-xxl)' }}>
              <label style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, color: 'var(--neutral-800)', display: 'block', marginBottom: 'var(--space-6)' }}>Password</label>
              <input
                type="password" value={password} onChange={e => setPassword(e.target.value)}
                placeholder="At least 8 characters" required style={inputStyle}
                onFocus={e => e.target.style.borderColor = '#d97706'}
                onBlur={e => e.target.style.borderColor = '#e8e4dc'}
              />
            </div>

            <button
              type="submit"
              disabled={loading || usernameStatus === 'checking' || usernameStatus === 'taken' || usernameStatus === 'invalid'}
              style={{
                width: '100%', padding: '13px', borderRadius: 'var(--radius-10)', border: 'none',
                background: (loading || usernameStatus !== 'available') ? 'var(--neutral-400)' : '#9E4B2C',
                color: 'var(--brand-paper)', fontSize: 'var(--font-size-md)', fontWeight: 700,
                cursor: (loading || usernameStatus !== 'available') ? 'default' : 'pointer',
              }}
            >
              {loading ? 'Creating account...' : 'Create account'}
            </button>
          </form>

          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', textAlign: 'center', margin: '16px 0 0', lineHeight: 1.5 }}>
            By signing up you agree to our{' '}
            <a href="/terms" target="_blank" style={{ color: 'var(--feedback-warning)', textDecoration: 'none' }}>Terms</a>{' '}and{' '}
            <a href="/privacy" target="_blank" style={{ color: 'var(--feedback-warning)', textDecoration: 'none' }}>Privacy Policy</a>
          </p>
          <div style={{ textAlign: 'center', marginTop: 'var(--space-lg)' }}>
            <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>
              Already have an account?{' '}
              <Link to="/login" style={{ color: 'var(--feedback-warning)', fontWeight: 600, textDecoration: 'none' }}>Log in</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── SVG Icons ────────────────────────────────────────────────────────────────

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
    </svg>
  );
}